"use client"

import { useEffect, useRef } from "react"
import { useCursorContext } from "../components/CursorProvider"

interface UseMagneticOptions {
  /** Pull strength: 0 = no pull, 1 = cursor snaps to element center */
  strength?: number
  /** Distance in px at which the pull begins */
  distance?: number
}

/**
 * Attaches a magnetic pull effect to an element: when the cursor comes within
 * `distance` pixels of the element's center, the element shifts toward the cursor.
 *
 * Usage:
 *   const ref = useMagnetic<HTMLButtonElement>()
 *   return <button ref={ref}>Hover me</button>
 */
export function useMagnetic<T extends HTMLElement = HTMLElement>({
  strength = 0.35,
  distance = 80,
}: UseMagneticOptions = {}) {
  const ref = useRef<T>(null)
  const { mousePosition } = useCursorContext()

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    const dx = mousePosition.x - centerX
    const dy = mousePosition.y - centerY
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist < distance) {
      const pull = (1 - dist / distance) * strength
      el.style.transform = `translate(${dx * pull}px, ${dy * pull}px)`
      el.style.transition = "transform 0.08s linear"
    } else {
      el.style.transform = "translate(0px, 0px)"
      el.style.transition = "transform 0.4s ease"
    }
  }, [mousePosition, strength, distance])

  // Reset on unmount
  useEffect(() => {
    return () => {
      if (ref.current) {
        ref.current.style.transform = ""
        ref.current.style.transition = ""
      }
    }
  }, [])

  return ref
}
